import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { Modal } from "react-bootstrap";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  fetchMyOrderDetailCard,
  fetchMyOrderTimeline,
} from "../../../../app/modules/Order/_redux/order/orderAction";
import {
  getOrderDocuments,
  orderDocumentDownloadUrl,
  uploadOrderDocument,
} from "../../../../app/modules/Order/_redux/order/orderCrud";
import { updateKycStatus } from "../../../../app/modules/KycList/_redux/QTasksCrud";
import downloadExport from "../../../../app/utils/downloadExport";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".pdf,.jpg,.jpeg,.png";

/**
 * What staff can do at each stage of ID verification and contract signing.
 *
 * `status` is the kyc status the action sends (the same values the Onboarding
 * list sends). It is the action the server runs, not always the status the
 * order ends at: `pending_access` sets the kyc status to `complete` and
 * queues the job that gives the customer access to their unit or keeps it
 * blocked. An action with `upload` needs a file first: the server refuses to
 * complete a manual ID or contract without one.
 */
const ACTIONS = {
  pending_kyc: {
    label: "Switch to manual ID check",
    title: "Switch to manual ID check",
    body:
      "The customer will be emailed that their ID is being checked manually. Once they send it, upload it here to confirm it.",
    confirm: "Switch to manual ID",
    status: "pending_manual_kyc",
  },
  pending_manual_kyc: {
    label: "Upload ID and confirm",
    title: "Confirm the customer's ID",
    body:
      "Upload the ID the customer sent. It is stored against this order, and the customer is then emailed the contract to sign.",
    confirm: "Upload and confirm ID",
    status: "pending_contract_sign",
    upload: "manual_id",
  },
  pending_contract_sign: {
    label: "Switch to manual contract",
    title: "Switch to manual contract",
    body:
      "Use this when the customer will sign and return the contract by email. Once they do, upload the signed copy here.",
    confirm: "Switch to manual contract",
    status: "pending_manual_contract_sign",
  },
  pending_manual_contract_sign: {
    label: "Upload signed contract and confirm",
    title: "Confirm the signed contract",
    body:
      "Upload the contract the customer signed. It is stored against this order. This marks the ID and contract checks complete. If the first invoice is paid, the customer is given access to their unit straight away. If it is not, access stays blocked and the customer may be emailed.",
    confirm: "Upload and confirm contract",
    status: "pending_access",
    upload: "manual_contract",
  },
};

/**
 * Manual ID and manual contract, done from the order page.
 *
 * Shows the one action that applies to the order's current stage, and the
 * files already uploaded against the order. Completing a manual ID or a manual
 * contract uploads the file first and only then changes the status.
 *
 * The actions are offered only to staff with `task.view`, the same permission
 * the Onboarding list needs to show them; everyone who can open the order
 * still sees the uploaded files.
 *
 * @param {Object} props
 * @param {Object} props.orderDetail The order (id, user_id, is_cancel, contract).
 * @returns {JSX.Element|null}
 */
export function ManualKycActions({ orderDetail }) {
  const dispatch = useDispatch();
  const { permission } = useSelector(
    (state) => ({ permission: state.auth.permission }),
    shallowEqual
  );
  const orderId = orderDetail?.id;
  const status = orderDetail?.contract?.kyc_status;
  const action =
    !permission?.task?.view || orderDetail?.is_cancel ? null : ACTIONS[status];

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [documents, setDocuments] = useState([]);
  const fileInput = useRef(null);
  // The file already uploaded in this dialog, so a retry after the status
  // change was refused does not upload it a second time.
  const uploadedFile = useRef(null);

  /** Load the files already uploaded against the order. */
  const loadDocuments = useCallback(() => {
    if (!orderId) return;
    getOrderDocuments(orderId)
      .then((res) => setDocuments(res?.data?.data || []))
      .catch(() => setDocuments([]));
  }, [orderId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  /** Close the dialog and clear what was picked. */
  const close = () => {
    if (busy) return;
    setOpen(false);
    setFile(null);
    uploadedFile.current = null;
    setError("");
  };

  /**
   * Keep the chosen file if it is small enough.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handleFile = (event) => {
    const picked = event.target.files?.[0] || null;
    if (picked && picked.size > MAX_BYTES) {
      setFile(null);
      setError("That file is over 10 MB. Please choose a smaller one.");
      return;
    }
    setFile(picked);
    uploadedFile.current = null;
    setError("");
  };

  /**
   * Upload the file if the action needs one, then move the order on. The
   * order and its timeline are reloaded whether or not the change succeeded.
   *
   * @returns {Promise<void>}
   */
  const handleConfirm = async () => {
    if (!action || busy) return;
    if (action.upload && !file) {
      setError("Choose the file to upload first.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      if (action.upload && uploadedFile.current !== file) {
        await uploadOrderDocument(orderId, action.upload, file);
        uploadedFile.current = file;
      }
      await updateKycStatus({
        user_id: orderDetail?.user_id,
        status: action.status,
        o_id: orderId,
      });
      setOpen(false);
      setFile(null);
      uploadedFile.current = null;
    } catch (err) {
      const fileError = err?.response?.data?.errors?.file?.[0];
      setError(
        fileError ||
          err?.response?.data?.message ||
          "That could not be saved. Please try again."
      );
    } finally {
      setBusy(false);
      // The upload may have succeeded even when the status change was refused,
      // and a refused status change can still have saved part of its work (a
      // refused manual ID is already marked verified), so reload either way.
      loadDocuments();
      dispatch(fetchMyOrderDetailCard(orderId));
      dispatch(fetchMyOrderTimeline(orderId));
    }
  };

  if (!orderId || (!action && documents.length === 0)) return null;

  return (
    <div className="mt-2">
      {action && (
        <button
          type="button"
          className="btn btn-sm btn-outline-warning"
          onClick={() => setOpen(true)}
        >
          {action.label}
        </button>
      )}

      {documents.length > 0 && (
        <div className="mt-2">
          {documents.map((document) => (
            <div key={document.id} className="font-size-sm">
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() =>
                  downloadExport(
                    orderDocumentDownloadUrl(orderId, document.id),
                    (message) => setError(message)
                  )
                }
              >
                {document.type_label}
              </button>
              <span className="text-muted">
                {" "}
                uploaded by {document.uploaded_by}
                {document.created_at
                  ? `, ${moment(document.created_at).format("DD-MM-YYYY HH:mm")}`
                  : ""}
              </span>
            </div>
          ))}
          {!open && error && <div className="text-danger">{error}</div>}
        </div>
      )}

      {action && (
        <Modal show={open} centered onHide={close}>
          <Modal.Header closeButton>
            <Modal.Title>{action.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{action.body}</p>
            {action.upload && (
              <div className="form-group mb-0">
                <label htmlFor="manual-kyc-file" className="font-weight-bold">
                  File (PDF, JPG or PNG, up to 10 MB)
                </label>
                <input
                  id="manual-kyc-file"
                  ref={fileInput}
                  type="file"
                  accept={ACCEPT}
                  className="form-control-file"
                  onChange={handleFile}
                  disabled={busy}
                />
              </div>
            )}
            {error && <div className="text-danger mt-3">{error}</div>}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-light btn-elevate"
              onClick={close}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-elevate"
              onClick={handleConfirm}
              disabled={busy || (action.upload && !file)}
            >
              {busy ? "Saving..." : action.confirm}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
